import type { CachedRelease, ReleaseStore } from "@aybuben/client/content/release-cache.ts";

const DB_NAME = "aybuben-content";
const DB_VERSION = 1;
const STORE = "releases";
const KEY = "current";

export class IdbReleaseStore implements ReleaseStore {
	private readonly factory: IDBFactory | undefined;

	constructor(factory?: IDBFactory) {
		// Injected in tests and honoured in the browser: `indexedDB` is absent in
		// some privacy modes, and a missing global must read as an empty cache
		// rather than a crash on a learner's first load.
		this.factory = factory ?? (typeof indexedDB === "undefined" ? undefined : indexedDB);
	}

	private open(): Promise<IDBDatabase | null> {
		const factory = this.factory;
		if (!factory) return Promise.resolve(null);
		return new Promise((resolve) => {
			let request: IDBOpenDBRequest;
			try {
				request = factory.open(DB_NAME, DB_VERSION);
			} catch {
				return resolve(null);
			}
			request.onupgradeneeded = () => {
				const db = request.result;
				if (!db.objectStoreNames.contains(STORE)) db.createObjectStore(STORE);
			};
			request.onsuccess = () => resolve(request.result);
			// A blocked or errored open is an unavailable cache, not a failure.
			request.addEventListener("error", () => resolve(null));
			request.onblocked = () => resolve(null);
		});
	}

	private async tx<T>(
		mode: IDBTransactionMode,
		fn: (store: IDBObjectStore) => IDBRequest<T>,
	): Promise<T | null> {
		const db = await this.open();
		if (!db) return null;
		return new Promise<T | null>((resolve) => {
			let request: IDBRequest<T>;
			try {
				request = fn(db.transaction(STORE, mode).objectStore(STORE));
			} catch {
				db.close();
				return resolve(null);
			}
			request.onsuccess = () => {
				const v = request.result;
				db.close();
				resolve(v);
			};
			request.addEventListener("error", () => {
				db.close();
				resolve(null);
			});
		});
	}

	async read(): Promise<unknown> {
		// Safe: `get` yields an IDBRequest<any>; the cache re-validates the value.
		return (await this.tx<unknown>("readonly", (s) => s.get(KEY) as IDBRequest<unknown>)) ?? null;
	}

	async write(r: CachedRelease): Promise<void> {
		// A single put is the atomic replace: IndexedDB transactions commit or
		// abort whole, so there is no window in which a reader sees a half-record.
		// Safe: the put request's result is a key; only success matters here.
		await this.tx("readwrite", (s) => s.put(r, KEY) as IDBRequest<IDBValidKey>);
	}

	async clear(): Promise<void> {
		// Safe: delete's request carries no result; only success matters here.
		await this.tx("readwrite", (s) => s.delete(KEY) as unknown as IDBRequest<undefined>);
	}
}
